import { List as ImmutableList } from 'immutable';
import { useMemo } from 'react';
import { DATA_WELL } from 'reporting-data/constants/chartTypes';
import { getDataType, getDimensions, getMetrics } from 'reporting-data/report/configReportGetters';
import { PRIOR_PERIOD } from 'reporting-data/constants/compareTypes';
import { getConfigMetricsForSummaryKpiMetricsEditor } from 'reporting-data/report/reportEditorsGetters';
import { AGGREGATION } from 'reporting-data/constants/configTypes';
import { UNIFIED_ADS_CAMPAIGNS, UNIFIED_ADS_NETWORKS } from 'reporting-data/constants/dataTypes';
// @ts-expect-error Untyped dependency
import CompareEditor from '../../../../../reportEditors/CompareEditor';
import { shouldDisableCompareOption } from '../../../../../reportEditors/common/comparisonHelpers';
import { splitMetricsIntoGroups, filterUnsupportedMetricsFromLimit } from './utils';
import useFetchContextualKPIs from './useFetchContextualKPIs/useFetchContextualKPIs';
import { NUMBER_OF_REPORTS_IN_CAROUSEL } from './constants';

// not a comprehensive list
const DATA_TYPES_WITHOUT_LIMIT_SUPPORT = [UNIFIED_ADS_NETWORKS, UNIFIED_ADS_CAMPAIGNS];
const getMetricsAndDimensionsForKpiReport = (processedReport, kpiLimit) => {
  const metricsFromSummaryMetricsEditor = getConfigMetricsForSummaryKpiMetricsEditor(processedReport);
  if (metricsFromSummaryMetricsEditor.size > 0) {
    return {
      metrics: metricsFromSummaryMetricsEditor,
      dimensions: ImmutableList()
    };
  }

  // we set limit: 1 for previews with a dimension in order to get back 1 KPI but
  // some dataTypes don't support limit and will return every breakdown
  const dataType = getDataType(processedReport);
  const supportsDimension = !(kpiLimit === 1 && DATA_TYPES_WITHOUT_LIMIT_SUPPORT.includes(dataType));
  const metrics = filterUnsupportedMetricsFromLimit(getMetrics(processedReport), kpiLimit);
  const reportDimensions = getDimensions(processedReport);
  const dimensionsArray = supportsDimension && metrics.size === 1 && reportDimensions.size > 1 ? [reportDimensions.get(1)] : [];
  return {
    metrics,
    dimensions: ImmutableList(dimensionsArray)
  };
};
const useConfigSummaryKpiReports = (processedReport, kpiLimit) => {
  const contextualKPIReports = useFetchContextualKPIs(processedReport, kpiLimit);
  const {
    metrics,
    dimensions
  } = useMemo(() => getMetricsAndDimensionsForKpiReport(processedReport, kpiLimit), [processedReport, kpiLimit]);
  const limitedMetrics = useMemo(() => kpiLimit ? metrics.take(kpiLimit) : metrics, [kpiLimit, metrics]);
  const baseDatawellReport = useMemo(() => {
    const isCompareAvailable = CompareEditor.isUsableForReport(processedReport);
    const isCompareDisabled = shouldDisableCompareOption(processedReport);
    return processedReport.withMutations(mutableReport => mutableReport.setIn(['config', 'configType'], AGGREGATION).setIn(['config', 'dimensions'], dimensions).setIn(['config', 'metrics'], limitedMetrics).set('chartType', DATA_WELL).updateIn(['config', 'limit'], limit => {
      if (dimensions.isEmpty()) {
        return limit;
      }
      return kpiLimit || NUMBER_OF_REPORTS_IN_CAROUSEL;
    }).updateIn(['config', 'compare'], compare => isCompareAvailable && isCompareDisabled ? PRIOR_PERIOD : compare));
  }, [kpiLimit, limitedMetrics, dimensions, processedReport]);
  return useMemo(() => contextualKPIReports.concat(splitMetricsIntoGroups(limitedMetrics, baseDatawellReport)), [limitedMetrics, baseDatawellReport, contextualKPIReports]);
};
export default useConfigSummaryKpiReports;